import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import JobDetailsModal from "@/components/JobDetailsModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Calendar,
  MapPin,
  DollarSign,
  Clock,
  Briefcase,
  MessageCircle,
  XCircle,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Eye,
  Loader2,
  Send,
  Building2,
  History,
  Users
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import "@/styles/commandDeck.css";

const MyApplicationsRedesigned = ({ onLogout }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [fullJobData, setFullJobData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawApp, setWithdrawApp] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'past'
  const [viewingApp, setViewingApp] = useState(null); // Track the application being viewed

  useEffect(() => {
    fetchApplications(1);
  }, []);

  const fetchApplications = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/my-applications`,
        {
          params: {
            user_email: user.email || 'test@shootzu.com',
            page,
            limit: 100 // Fetch more to filter client-side
          }
        }
      );

      const newApps = response.data.applications || [];

      if (page === 1) {
        setApplications(newApps);
      } else {
        setApplications(prev => [...prev, ...newApps]);
      }

      setCurrentPage(page);
      setHasMore(newApps.length === 100);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
      if (page === 1) setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  // Filter applications based on active tab
  const isActiveApplication = (app) => {
    const jobStatus = (app.job_status || '').toUpperCase();
    const appStatus = app.application_status;
    const isSeekingApplicants = jobStatus === 'SEEKING_APPLICANTS' || jobStatus === 'OPEN' || jobStatus === 'ACTIVE';
    // Accepted/hired applications are active (upcoming bookings), not past
    const isHired = appStatus === 'accepted';
    return isSeekingApplicants || isHired;
  };

  const filteredApplications = applications.filter(app => {
    if (activeTab === 'active') {
      return isActiveApplication(app);
    } else {
      return !isActiveApplication(app);
    }
  }).sort((a, b) => {
    // For past applications, sort by application date (newest first)
    if (activeTab === 'past') {
      const dateA = new Date(a.applied_at || a.created_at || 0);
      const dateB = new Date(b.applied_at || b.created_at || 0);
      return dateB - dateA;
    }
    return 0;
  });

  // Count for tabs
  const activeCount = applications.filter(app => isActiveApplication(app)).length;

  const pastCount = applications.length - activeCount;

  const handleWithdrawClick = useCallback((app) => {
    setWithdrawApp(app);
    setShowWithdrawModal(true);
  }, []);

  const handleWithdrawConfirm = useCallback(async () => {
    if (!withdrawApp) return;

    try {
      setWithdrawing(withdrawApp.id);
      setShowWithdrawModal(false);

      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${withdrawApp.id}`,
        {
          params: { user_email: user.email }
        }
      );

      if (response.data.success) {
        toast.success('Application withdrawn successfully');
        // Remove from list
        setApplications(prev => prev.filter(app => app.id !== withdrawApp.id));
      }
    } catch (error) {
      console.error('Failed to withdraw application:', error);
      toast.error(error.response?.data?.detail || 'Failed to withdraw application');
    } finally {
      setWithdrawing(null);
      setWithdrawApp(null);
    }
  }, [withdrawApp, user.email]);

  const handleMessage = useCallback(async (ownerEmail, jobId, jobTitle) => {
    if (!ownerEmail) {
      toast.error('Unable to contact job owner');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/create`,
        {
          job_id: jobId,
          job_title: jobTitle,
          user1_email: user.email,
          user2_email: ownerEmail
        }
      );

      // Get conversation ID from response
      const conversation = response.data.conversation;
      const conversationId = response.data.conversation_id || conversation?.id || conversation?._id;

      if (conversationId || conversation) {
        toast.success('Opening conversation...');
        navigate('/messages', {
          state: {
            conversationId: conversationId || conversation.id,
            autoOpen: true
          }
        });
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    }
  }, [user.email, navigate]);

  const handleViewDetails = useCallback(async (jobId, app = null) => {
    setViewingApp(app);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/details/${jobId}`
      );
      setFullJobData(response.data);
      setShowJobDetails(true);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');
    }
  }, []);

  // Command Deck palette tokens
  const ST = {
    pending:   { color: '#FCD34D', bg: 'rgba(180,83,9,.22)',   icon: AlertCircle },
    hired:     { color: '#22D3EE', bg: 'rgba(14,116,144,.25)',  icon: CheckCircle },
    completed: { color: '#60A5FA', bg: 'rgba(37,99,235,.22)',   icon: CheckCircle },
    danger:    { color: '#F87171', bg: 'rgba(185,28,28,.2)',    icon: XCircle },
    neutral:   { color: 'rgba(255,255,255,.6)', bg: 'rgba(255,255,255,.06)', icon: XCircle }
  };

  const getStatusConfig = (app) => {
    const jobStatus = (app.job_status || '').toUpperCase();
    const appStatus = app.application_status;

    // For past applications tab - show specific status labels
    if (activeTab === 'past') {
      // Check if job was cancelled
      if (jobStatus === 'CANCELLED') {
        return { ...ST.neutral, label: 'Job Cancelled' };
      }

      // Check if user was hired (accepted)
      if (appStatus === 'accepted') {
        return { ...ST.hired, label: 'Hired' };
      }

      // Check if job is completed
      if (appStatus === 'completed' || jobStatus === 'PAID_COMPLETED' || jobStatus === 'COMPLETED') {
        return { ...ST.completed, label: 'Completed' };
      }

      // Job was hired but not this user = unsuccessful
      if (jobStatus === 'HIRED' || jobStatus === 'PENDING_COMPLETION' || jobStatus === 'RELEASE_PAYMENT' || jobStatus === 'OFFER_SENT') {
        return { ...ST.danger, label: 'Unsuccessful' };
      }

      // Default for past - unsuccessful
      return { ...ST.danger, label: 'Unsuccessful' };
    }

    // For active applications tab
    const configs = {
      pending:   { ...ST.pending,   label: 'Pending Review' },
      accepted:  { ...ST.hired,     label: 'Accepted - You\'re Hired!' },
      cancelled: { ...ST.danger,    label: 'Job Cancelled' },
      completed: { ...ST.completed, label: 'Job Completed' }
    };
    return configs[appStatus] || configs.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'TBD' || dateString === 'TBC') return 'TBD';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'TBD';
      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return 'TBD';
    }
  };

  const getCrewPayout = (amount) => {
    return Math.floor(amount * 0.98);
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main">
          {/* Header */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
            <div>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#28C0D0' }}>YOUR PIPELINE</div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: '-1px', color: '#fff', marginTop: 8 }}>My applications</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#28C0D0', background: 'rgba(40,192,208,.14)', padding: '7px 13px', borderRadius: 999 }}>
                <TrendingUp className="w-3.5 h-3.5" />
                {activeCount} Active
              </span>
              <button type="button" onClick={() => navigate('/find-jobs')} className="cd-btn cd-btn-primary" style={{ padding: '13px 22px', fontSize: 14 }}>
                <Briefcase className="w-[18px] h-[18px]" />
                Browse jobs
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="cd-tabs" style={{ width: 'max-content', marginBottom: 18 }}>
            <button type="button" onClick={() => setActiveTab('active')} className={`cd-tab ${activeTab === 'active' ? 'cd-tab-active' : ''}`}>
              <Briefcase className="w-4 h-4" />
              Active{activeCount > 0 && <span className="cd-tab-count">{activeCount}</span>}
            </button>
            <button type="button" onClick={() => setActiveTab('past')} className={`cd-tab ${activeTab === 'past' ? 'cd-tab-active' : ''}`}>
              <History className="w-4 h-4" />
              Past{pastCount > 0 && <span className="cd-tab-count">{pastCount}</span>}
            </button>
          </div>

          {/* States */}
          {loading && currentPage === 1 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="cd-skel">
                  <span style={{ width: 90, height: 64, borderRadius: 12, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: 120, height: 11, borderRadius: 6, background: 'rgba(255,255,255,.08)', marginBottom: 10 }} />
                    <div style={{ width: '55%', height: 16, borderRadius: 6, background: 'rgba(255,255,255,.1)', marginBottom: 10 }} />
                    <div style={{ width: '38%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,.07)' }} />
                  </div>
                  <div style={{ width: 80, height: 36, borderRadius: 10, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                </div>
              ))}
            </div>
          ) : filteredApplications.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 18 }}>
              <span style={{ width: 64, height: 64, borderRadius: 18, background: 'rgba(40,192,208,.12)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                {activeTab === 'active' ? <Briefcase className="w-[30px] h-[30px]" /> : <History className="w-[30px] h-[30px]" />}
              </span>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>
                {activeTab === 'active' ? 'No active applications' : 'No past applications'}
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.55)', marginTop: 7, maxWidth: 360, lineHeight: 1.55 }}>
                {activeTab === 'active'
                  ? "You don't have any pending applications for jobs currently seeking applicants. Find a gig and apply — they'll show up here."
                  : 'Your completed, unsuccessful, or cancelled applications will appear here.'}
              </div>
              {activeTab === 'active' && (
                <button type="button" onClick={() => navigate('/find-jobs')} className="cd-btn cd-btn-primary" style={{ marginTop: 20, padding: '12px 22px', fontSize: 13.5 }}>
                  Browse available jobs
                </button>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {filteredApplications.map((app) => {
                  const statusConfig = getStatusConfig(app);
                  const StatusIcon = statusConfig.icon;
                  const crewPayout = getCrewPayout(app.total_amount || 0);
                  const hourlyRate = getCrewPayout(app.pay_rate || 0);
                  const isAccepted = app.application_status === 'accepted';
                  const canWithdraw = activeTab === 'active' && app.application_status === 'pending';
                  const initials = (app.owner_name || 'U').charAt(0).toUpperCase();
                  const panelGrad = isAccepted
                    ? 'linear-gradient(150deg,rgba(14,116,144,.35),rgba(40,192,208,.12))'
                    : 'linear-gradient(150deg,rgba(87,43,124,.4),rgba(120,40,124,.2))';

                  return (
                    <div key={app.id} className="cd-card cd-col-mobile" style={{ alignItems: 'stretch' }}>
                      {/* Left — Job info */}
                      <div style={{ flex: 1, minWidth: 0, padding: '20px 22px' }}>
                        {/* Owner row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                          <span style={{ width: 42, height: 42, borderRadius: 11, background: 'linear-gradient(135deg,#572B7C,#28C0D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, flex: '0 0 auto', overflow: 'hidden' }} className="cd-display">
                            {app.owner_photo ? (
                              <img src={app.owner_photo} alt={app.owner_name || 'Job Owner'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : initials}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {app.owner_name || 'Unknown Employer'}
                            </div>
                            <div className="cd-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>
                              Job ID {app.job_number || 'N/A'}
                            </div>
                          </div>
                          <span className="cd-badge" style={{ color: statusConfig.color, background: statusConfig.bg, flex: '0 0 auto' }}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </div>

                        {/* Title */}
                        <div className="cd-display" style={{ fontWeight: 700, fontSize: 18, color: '#fff' }}>
                          {app.job_title}
                        </div>
                        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)', marginTop: 4 }}>
                          {app.role ? `Applied as ${app.role.replace(/_/g, ' ')}` : 'Applied'}
                          {(app.applied_at || app.created_at) ? ` · ${formatDate(app.applied_at || app.created_at)}` : ' · date pending'}
                        </div>

                        {/* Details */}
                        <div className="cd-row" style={{ display: 'flex', gap: 28, marginTop: 16, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Calendar className="w-3 h-3" />Job date
                            </div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', marginTop: 2 }}>{formatDate(app.job_date)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Clock className="w-3 h-3" />Duration
                            </div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', marginTop: 2 }}>
                              {app.hours && app.hours > 0 ? `${app.hours} hours` : 'TBD'}
                            </div>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <MapPin className="w-3 h-3" />Location
                            </div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {app.location || 'TBD'}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="cd-row" style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => handleViewDetails(app.job_id, app)} className="cd-btn" style={{ padding: '10px 16px', fontSize: 12.5 }}>
                            <Eye className="w-4 h-4" />
                            View full details
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMessage(app.owner_email, app.job_id, app.job_title)}
                            disabled={!app.owner_email}
                            className="cd-btn"
                            style={{ padding: '10px 16px', fontSize: 12.5, background: 'transparent', opacity: app.owner_email ? 1 : 0.5 }}
                          >
                            <Send className="w-4 h-4" />
                            Message employer
                          </button>
                          {canWithdraw && (
                            <button
                              type="button"
                              onClick={() => handleWithdrawClick(app)}
                              disabled={withdrawing === app.id}
                              className="cd-btn"
                              style={{ padding: '10px 16px', fontSize: 12.5, background: 'transparent', border: '1px solid rgba(185,28,28,.35)', color: '#F87171' }}
                            >
                              {withdrawing === app.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Withdrawing...
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-4 h-4" />
                                  Withdraw
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Right — Earnings panel */}
                      <div className="cd-w-full-m" style={{ flex: '0 0 230px', background: panelGrad, borderLeft: '1px solid rgba(255,255,255,.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, textAlign: 'center' }}>
                        {crewPayout > 0 ? (
                          <>
                            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)' }}>
                              {isAccepted ? 'Confirmed payout' : "You'll earn"}
                            </div>
                            <div className="cd-display" style={{ fontWeight: 800, fontSize: 36, color: '#fff', letterSpacing: '-1px', lineHeight: 1.1 }}>
                              ${crewPayout.toLocaleString()}
                            </div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
                              ${hourlyRate}/hr × {app.hours}h
                            </div>
                            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.4)', marginTop: 6 }}>after 2% fee</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)' }}>Your earnings</div>
                            <div className="cd-display" style={{ fontWeight: 800, fontSize: 30, color: 'rgba(255,255,255,.6)', letterSpacing: '-1px', lineHeight: 1.1, marginTop: 4 }}>TBD</div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 4 }}>Pending job details</div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load More */}
              {!loading && hasMore && filteredApplications.length > 0 && (
                <div style={{ marginTop: 24, textAlign: 'center' }}>
                  <button type="button" onClick={() => fetchApplications(currentPage + 1)} className="cd-btn" style={{ padding: '13px 28px', fontSize: 13.5 }}>
                    Load more applications
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>


      {/* Job Details Modal */}
      {showJobDetails && fullJobData && (
        <JobDetailsModal
          job={fullJobData}
          isOpen={showJobDetails}
          onClose={() => {
            setShowJobDetails(false);
            setFullJobData(null);
            setViewingApp(null);
          }}
          onMessageOwner={() => handleMessage(fullJobData.owner_email, fullJobData.id, fullJobData.title)}
          isDark={isDark}
          showCrewPayout={true}
          hideHiredCrew={true}
        />
      )}

      {/* Withdraw Confirmation Modal */}
      {showWithdrawModal && withdrawApp && (
        <div className="cd-modal-overlay">
          <div
            className="cd-modal-card cd-root"
            style={{ maxWidth: 430 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ position: 'relative', padding: '26px 26px 22px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => { setShowWithdrawModal(false); setWithdrawApp(null); }}
                className="cd-modal-close"
                aria-label="Close"
              >
                <XCircle className="w-[17px] h-[17px]" />
              </button>
              <div style={{ width: 60, height: 60, borderRadius: 18, margin: '4px auto 14px', background: 'rgba(185,28,28,.14)', border: '1px solid rgba(185,28,28,.34)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F87171' }}>
                <AlertCircle className="w-7 h-7" />
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: '-.4px' }}>
                Withdraw application?
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', marginTop: 8, lineHeight: 1.55 }}>
                Your application for <span style={{ color: '#fff', fontWeight: 600 }}>{withdrawApp.job_title}</span> will be removed. The employer will be notified, and you can re-apply while the job is still open.
              </div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.45)', marginTop: 10 }}>
                Posted by {withdrawApp.owner_name || 'Unknown Employer'}
              </div>
            </div>

            {/* Actions */}
            <div style={{ padding: '4px 26px 24px', display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => { setShowWithdrawModal(false); setWithdrawApp(null); }}
                className="cd-btn"
                style={{ flex: 1, padding: 13, fontSize: 14, background: 'transparent' }}
              >
                Keep it
              </button>
              <button
                type="button"
                onClick={handleWithdrawConfirm}
                className="cd-btn"
                style={{ flex: 1.3, padding: 13, fontSize: 14, fontWeight: 700, background: 'rgba(185,28,28,.16)', border: '1px solid rgba(185,28,28,.4)', color: '#F87171' }}
              >
                Withdraw
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApplicationsRedesigned;
